import { legOrdinal } from '../legGroups'
import { useQuoteI18n } from '../quoteI18n'

/**
 * Riepilogo del carrello e unico punto di partenza della prenotazione.
 *
 * I servizi scelti diventano **un solo trip con più segmenti**: una compilazione dei dati
 * richiesti, una conferma, un codice viaggio. Per questo il pulsante è uno solo e sta qui,
 * non sulle singole offerte.
 *
 * @param {Object} props
 * @param {Array} props.selections - Offerte scelte, una per tratta
 * @param {Array} props.legGroups - Gruppi di tratte disponibili
 * @param {boolean} props.allLegsSelected - True se ogni tratta ha una selezione
 * @param {boolean} props.submitting - True mentre la richiesta parte
 * @param {() => void} props.onBook - Avvia la prenotazione dei servizi scelti
 */
export function SelectionSummary({
  id,
  selections,
  legGroups,
  allLegsSelected,
  submitting,
  onBook
}) {
  const { t } = useQuoteI18n()
  const total = selections.reduce((sum, offer) => sum + (offer.price || 0), 0)
  const currency = selections[0]?.currencyCode === 'EUR' ? '€' : selections[0]?.currencyCode || ''
  const empty = selections.length === 0
  const totalLegs = legGroups?.length || 0
  const missingCount = totalLegs - selections.length
  const hasLocalTax = selections.some((offer) => !!offer.details?.localTaxInfo)

  return (
    <div id={id} className="gq-cart">
      <h3 className="gq-cart__title">{t.cart.title}</h3>

      {empty ? (
        <p className="gq-cart__empty">{t.cart.empty}</p>
      ) : (
        <>
          <ul className="gq-cart__list">
            {selections.map((offer) => (
              <li className="gq-cart__row" key={offer.offerId}>
                <span>
                  <strong>{segmentLabel(t, offer)}</strong> — {offer.summary}
                </span>
                <span className="gq-cart__row-price">
                  {currency} {offer.price?.toFixed(2)}
                  <PriceBasis offer={offer} />
                </span>
              </li>
            ))}
          </ul>
          <div className="gq-cart__total">
            <span>{t.cart.total}</span>
            <span>
              {currency} {total.toFixed(2)}
            </span>
          </div>
          {hasLocalTax && <p className="gq-cart__caveat">{t.cart.taxesExcluded}</p>}
        </>
      )}

      {!empty && !allLegsSelected && (
        <p className="gq-cart__missing" role="status">
          {t.cart.missingLegs(missingCount)}
        </p>
      )}

      <button
        type="button"
        className="gq-btn gq-btn--primary gq-btn--block"
        onClick={onBook}
        disabled={!allLegsSelected || submitting}
      >
        {submitting ? t.cart.submitting : buttonLabel(t, selections.length)}
      </button>

      {!empty && <p className="gq-cart__note">{t.cart.note}</p>}
    </div>
  )
}

/**
 * Etichetta della riga: il servizio, più ciò che identifica la tratta.
 *
 * @param {Object} t - Dizionario tradotto
 * @param {Object} offer - Offerta scelta
 * @returns {string} Etichetta leggibile
 */
function segmentLabel(t, offer) {
  const parts = [t.cart.segment[offer.segmentType] || offer.segmentType]
  const ordinal = legOrdinal(offer.legKey)
  if (ordinal && t.services?.leg) parts.push(t.services.leg(ordinal))
  if (offer.details?.direction) parts.push(t.offer[offer.details.direction])
  return parts.join(' · ')
}

/**
 * Da cosa deriva il prezzo di una riga, quando non è il totale pubblicato dal fornitore.
 *
 * Vale per l'auto: Goelett la quota al giorno e la normalizzazione moltiplica per i giorni
 * di noleggio. Senza questa riga il carrello mostrerebbe un numero che il viaggiatore non
 * ritrova sul sito del noleggiatore.
 */
function PriceBasis({ offer }) {
  const { t } = useQuoteI18n()
  const perDay = offer.details?.pricePerDay
  const days = offer.details?.rentalDays
  if (offer.segmentType !== 'Car' || perDay == null || !days) return null
  const currency = offer.currencyCode === 'EUR' ? '€' : offer.currencyCode || ''
  return (
    <span className="gq-cart__row-basis">
      {t.offer.priceBreakdown(
        `${currency} ${perDay.toFixed(2)} / ${t.offer.perDayShort}`,
        t.offer.days(days)
      )}
    </span>
  )
}

function buttonLabel(t, count) {
  if (count > 1) return t.cart.continueWith(count)
  if (count === 1) return t.cart.continueOne
  return t.cart.continue
}
